import { useWorkspace } from '@/providers';
import { Button } from './ui/button';
import { Spinner } from './ui/spinner';

/**
 * Pause Workspace confirmation dialog component.
 * Displays a modal asking the user to confirm pausing a workspace.
 */
export function PauseWorkspaceDialog() {
  const { dialogs } = useWorkspace();
  const { stop } = dialogs;

  if (!stop.isOpen || !stop.workspace) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={dialogs.cancelStop}
    >
      <div
        className="mx-4 w-full max-w-md rounded-lg border border-gray-200 bg-white p-5 shadow-lg dark:border-white/10 dark:bg-gray-800"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="mb-1.5 font-semibold text-[16px] text-gray-900 dark:text-gray-200">Pause Workspace</h3>
        <p className="mb-4 text-[13px] text-gray-600 dark:text-gray-400">
          Are you sure you want to pause{' '}
          <span className="font-medium text-gray-900 dark:text-gray-200">{stop.workspace.name}</span>? Your data will be
          preserved.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={dialogs.cancelStop} disabled={stop.isLoading}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={dialogs.confirmStop}
            disabled={stop.isLoading}
            className="bg-orange-600 text-white hover:bg-orange-700"
          >
            {stop.isLoading && <Spinner size="h-3.5 w-3.5" className="mr-1.5" />}
            Pause
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Delete Workspace confirmation dialog component.
 * Displays a modal asking the user to confirm deleting a workspace.
 */
export function DeleteWorkspaceDialog() {
  const { dialogs } = useWorkspace();
  const { terminate } = dialogs;

  if (!terminate.isOpen || !terminate.workspace) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={dialogs.cancelTerminate}
    >
      <div
        className="mx-4 w-full max-w-md rounded-lg border border-gray-200 bg-white p-5 shadow-lg dark:border-white/10 dark:bg-gray-800"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="mb-1.5 font-semibold text-[16px] text-gray-900 dark:text-gray-200">Delete Workspace</h3>
        <p className="mb-4 text-[13px] text-gray-600 dark:text-gray-400">
          Are you sure you want to delete{' '}
          <span className="font-medium text-gray-900 dark:text-gray-200">{terminate.workspace.name}</span>? This will
          permanently delete all data.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={dialogs.cancelTerminate} disabled={terminate.isLoading}>
            Cancel
          </Button>
          <Button variant="destructive" size="sm" onClick={dialogs.confirmTerminate} disabled={terminate.isLoading}>
            {terminate.isLoading && <Spinner size="h-3.5 w-3.5" className="mr-1.5" />}
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Combined workspace dialogs component.
 * Renders both Pause and Delete confirmation dialogs.
 */
export function WorkspaceDialogs() {
  return (
    <>
      <PauseWorkspaceDialog />
      <DeleteWorkspaceDialog />
    </>
  );
}
